import { motion, useReducedMotion } from "framer-motion";

/** Desaceleración exponencial: llegadas seguras, sin rebote. */
export const EASE = [0.16, 1, 0.3, 1];

/**
 * Entradas al hacer scroll. El estado por defecto es visible: si el script
 * falla o el usuario pidió menos movimiento, el contenido está ahí igual.
 *
 * Variantes (cada sección usa la que le corresponde, no todas la misma):
 *   rise — el bloque llega desde abajo. Contenido que entra en escena.
 *   mask — el texto se descubre tras una máscara. Titulares y cifras.
 *   draw — una regla se traza de izquierda a derecha. Separadores y pasos.
 */
export default function Reveal({
    variant = "rise",
    delay = 0,
    className = "",
    children,
    ...rest
}) {
    const reduce = useReducedMotion();
    // amount: 0 dispara en cuanto asoma un pixel. Importante: la variante
    // `mask` esconde el contenido hasta que la animación corre, así que el
    // disparo tiene que ser lo más temprano y fiable posible.
    const viewport = { once: true, amount: 0, margin: "-48px" };

    if (reduce) {
        return (
            <div className={className} {...rest}>
                {children}
            </div>
        );
    }

    if (variant === "mask") {
        // El padding protege las colas de las letras (p, y, g) del recorte.
        return (
            <span
                className={`block overflow-hidden pb-[0.12em] -mb-[0.12em] ${className}`}
                {...rest}
            >
                <motion.span
                    className="block"
                    initial={{ y: "108%" }}
                    whileInView={{ y: 0 }}
                    viewport={viewport}
                    transition={{ duration: 0.8, delay, ease: EASE }}
                >
                    {children}
                </motion.span>
            </span>
        );
    }

    if (variant === "draw") {
        return (
            <motion.div
                className={`origin-left ${className}`}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={viewport}
                transition={{ duration: 0.7, delay, ease: EASE }}
                {...rest}
            >
                {children}
            </motion.div>
        );
    }

    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ duration: 0.6, delay, ease: EASE }}
            {...rest}
        >
            {children}
        </motion.div>
    );
}
